/**
 * SiteInput Component - Updated
 * ==============================
 * Input panel with tabs for Periodontal AND Restorative data.
 * 
 * Replace your existing frontend/src/components/charting/SiteInput.tsx with this file.
 */

import { useState, useRef, useEffect } from 'react';
import { useChartStore, useSelectedToothData, useIsChartEditable } from '@/stores/chartStore';
import { getToothInfo, hasFurcation } from '@/lib/tooth-numbering';
import { Input } from '@/components/ui/Input';
import { RestorativeInput } from './RestorativeInput';
import { cn } from '@/lib/utils';
import type { SiteCode, SiteData, ToothData } from '@/types';

interface SiteInputProps {
  toothNumber: number;
}

type TabType = 'periodontal' | 'restorative';

const BUCCAL_SITES: SiteCode[] = ['MB', 'B', 'DB'];
const LINGUAL_SITES: SiteCode[] = ['ML', 'L', 'DL'];

export function SiteInput({ toothNumber }: SiteInputProps) {
  const toothData = useSelectedToothData();
  const isEditable = useIsChartEditable();
  const { updateToothData, updateSiteData, selectTooth, selectedSite, selectSite } = useChartStore();
  const toothInfo = getToothInfo(toothNumber);
  const canHaveFurcation = hasFurcation(toothNumber);

  const [activeTab, setActiveTab] = useState<TabType>('periodontal');
  const [localMobility, setLocalMobility] = useState<string>(
    toothData?.mobility?.toString() || ''
  );
  const [localFurcation, setLocalFurcation] = useState<string>(
    toothData?.furcation?.toString() || ''
  );

  // Site input refs for keyboard navigation
  const siteInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    setLocalMobility(toothData?.mobility?.toString() || '');
    setLocalFurcation(toothData?.furcation?.toString() || '');
  }, [toothNumber, toothData]);

  const getSiteData = (siteCode: SiteCode): SiteData | undefined => {
    return toothData?.sites?.find((s) => s.site === siteCode);
  };

  const handleSiteValueChange = (
    siteCode: SiteCode,
    field: keyof SiteData,
    value: number | boolean | null
  ) => {
    if (!isEditable) return;
    updateSiteData(toothNumber, siteCode, { [field]: value });
  };

  const handleMobilityChange = (value: string) => {
    setLocalMobility(value);
    const numValue = value === '' ? null : parseInt(value, 10);
    if (numValue === null || (numValue >= 0 && numValue <= 3)) {
      updateToothData(toothNumber, { mobility: numValue });
    }
  };

  const handleFurcationChange = (value: string) => {
    setLocalFurcation(value);
    const numValue = value === '' ? null : parseInt(value, 10);
    if (numValue === null || (numValue >= 0 && numValue <= 3)) {
      updateToothData(toothNumber, { furcation: numValue });
    }
  };

  const handleStatusChange = (status: ToothData['status']) => {
    if (!isEditable) return;
    updateToothData(toothNumber, { status });
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    siteCode: SiteCode,
    field: 'pocket_depth' | 'gingival_margin'
  ) => {
    const allSites = [...BUCCAL_SITES, ...LINGUAL_SITES];
    const currentIndex = allSites.indexOf(siteCode);

    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
      
      if (nextIndex >= 0 && nextIndex < allSites.length) {
        const nextSite = allSites[nextIndex];
        const nextRef = siteInputRefs.current[`${nextSite}-${field}`];
        nextRef?.focus();
        selectSite(nextSite);
      }
    }

    if (e.key === 'ArrowDown' && field === 'pocket_depth') {
      const gmRef = siteInputRefs.current[`${siteCode}-gingival_margin`];
      gmRef?.focus();
    }

    if (e.key === 'ArrowUp' && field === 'gingival_margin') {
      const pdRef = siteInputRefs.current[`${siteCode}-pocket_depth`];
      pdRef?.focus();
    }
  };

  // Check if tooth has restorative data for badge
  const hasRestorativeData = Boolean(
    toothData?.restorative?.crown?.present ||
    (toothData?.restorative?.fillings?.length || 0) > 0 ||
    toothData?.restorative?.caries?.present ||
    toothData?.restorative?.root_canal?.treated ||
    toothData?.restorative?.treatment_needed?.procedure
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Tooth #{toothNumber}
            </h3>
            <p className="text-sm text-gray-500">{toothInfo?.name}</p>
          </div>
          <button
            onClick={() => selectTooth(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Status */}
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <div className="flex flex-wrap gap-2">
            {(['present', 'missing', 'implant', 'crown', 'pontic'] as ToothData['status'][]).map((status) => (
              <button
                key={status}
                onClick={() => handleStatusChange(status)}
                disabled={!isEditable}
                className={cn(
                  'px-3 py-1 text-sm rounded-full border transition-colors capitalize',
                  toothData?.status === status
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300',
                  !isEditable && 'opacity-50 cursor-not-allowed'
                )}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        {toothData?.status !== 'missing' && (
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('periodontal')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'periodontal'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              Periodontal
            </button>
            <button
              onClick={() => setActiveTab('restorative')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
                activeTab === 'restorative'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              Restorative
              {hasRestorativeData && (
                <span className="w-2 h-2 rounded-full bg-yellow-400" />
              )}
            </button>
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-4">
        {toothData?.status === 'missing' ? (
          <div className="text-center text-gray-500 py-8">
            <p>This tooth is marked as missing.</p>
            <p className="text-sm mt-2">Change status to record measurements.</p>
          </div>
        ) : activeTab === 'periodontal' ? (
          /* Periodontal Tab */
          <div className="space-y-4">
            {/* Mobility & Furcation */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobility (0-3)
                </label>
                <Input
                  type="number"
                  min="0"
                  max="3"
                  value={localMobility}
                  onChange={(e) => handleMobilityChange(e.target.value)}
                  disabled={!isEditable}
                  className="w-full"
                />
              </div>
              {canHaveFurcation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Furcation (0-3)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="3"
                    value={localFurcation}
                    onChange={(e) => handleFurcationChange(e.target.value)}
                    disabled={!isEditable}
                    className="w-full"
                  />
                </div>
              )}
            </div>

            {/* Buccal Sites */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Buccal (Facial)</h4>
              <div className="grid grid-cols-3 gap-2">
                {BUCCAL_SITES.map((site) => (
                  <SiteInputField
                    key={site}
                    siteCode={site}
                    siteData={getSiteData(site)}
                    isSelected={selectedSite === site}
                    isEditable={isEditable}
                    onSelect={() => selectSite(site)}
                    onChange={(field, value) => handleSiteValueChange(site, field, value)}
                    onKeyDown={(e, field) => handleKeyDown(e, site, field)}
                    inputRef={(el, field) => {
                      siteInputRefs.current[`${site}-${field}`] = el;
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Lingual Sites */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Lingual (Palatal)</h4>
              <div className="grid grid-cols-3 gap-2">
                {LINGUAL_SITES.map((site) => (
                  <SiteInputField
                    key={site}
                    siteCode={site}
                    siteData={getSiteData(site)}
                    isSelected={selectedSite === site}
                    isEditable={isEditable}
                    onSelect={() => selectSite(site)}
                    onChange={(field, value) => handleSiteValueChange(site, field, value)}
                    onKeyDown={(e, field) => handleKeyDown(e, site, field)}
                    inputRef={(el, field) => {
                      siteInputRefs.current[`${site}-${field}`] = el;
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Restorative Tab */
          <RestorativeInput toothNumber={toothNumber} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface SiteInputFieldProps {
  siteCode: SiteCode;
  siteData?: SiteData;
  isSelected: boolean;
  isEditable: boolean;
  onSelect: () => void;
  onChange: (field: keyof SiteData, value: number | boolean | null) => void;
  onKeyDown: (e: React.KeyboardEvent, field: 'pocket_depth' | 'gingival_margin') => void;
  inputRef: (el: HTMLInputElement | null, field: string) => void;
}

function SiteInputField({
  siteCode,
  siteData,
  isSelected,
  isEditable,
  onSelect,
  onChange,
  onKeyDown,
  inputRef,
}: SiteInputFieldProps) {
  return (
    <div
      className={cn(
        'p-2 rounded border transition-colors',
        isSelected ? 'border-primary-300 bg-primary-50' : 'border-gray-200'
      )}
      onClick={onSelect}
    >
      <div className="text-xs font-medium text-gray-500 mb-1 text-center">
        {siteCode}
      </div>
      
      {/* Pocket Depth */}
      <input
        ref={(el) => inputRef(el, 'pocket_depth')}
        type="number"
        min="0"
        max="15"
        placeholder="PD"
        value={siteData?.pocket_depth ?? ''}
        onChange={(e) => {
          const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
          onChange('pocket_depth', val);
        }}
        onKeyDown={(e) => onKeyDown(e, 'pocket_depth')}
        disabled={!isEditable}
        className={cn(
          'w-full text-center text-sm border rounded px-1 py-0.5 mb-1',
          siteData?.pocket_depth && siteData.pocket_depth >= 5
            ? 'border-red-300 bg-red-50'
            : 'border-gray-200'
        )}
      />
      
      {/* Gingival Margin */}
      <input
        ref={(el) => inputRef(el, 'gingival_margin')}
        type="number"
        min="-10"
        max="10"
        placeholder="GM"
        value={siteData?.gingival_margin ?? ''}
        onChange={(e) => {
          const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
          onChange('gingival_margin', val);
        }}
        onKeyDown={(e) => onKeyDown(e, 'gingival_margin')}
        disabled={!isEditable}
        className="w-full text-center text-sm border border-gray-200 rounded px-1 py-0.5 mb-1"
      />
      
      {/* BOP & Suppuration */}
      <div className="flex justify-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onChange('bleeding', !siteData?.bleeding);
          }}
          disabled={!isEditable}
          className={cn(
            'w-6 h-6 text-xs rounded',
            siteData?.bleeding
              ? 'bg-clinical-bleeding text-white'
              : 'bg-gray-100 text-gray-400'
          )}
          title="Bleeding on Probing"
        >
          B
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onChange('suppuration', !siteData?.suppuration);
          }}
          disabled={!isEditable}
          className={cn(
            'w-6 h-6 text-xs rounded',
            siteData?.suppuration
              ? 'bg-clinical-suppuration text-white'
              : 'bg-gray-100 text-gray-400'
          )}
          title="Suppuration"
        >
          S
        </button>
      </div>
    </div>
  );
}

export default SiteInput;
